'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Icon } from '@/components/icon';

/**
 * Accordion — the existing Radix Accordion restyled to Figma `Accordion` (`3563:21575`) for
 * CRCB2B-631, with the PDP usage at `13869:82363`. **Restyle, not replacement**: every export
 * and prop keeps its name so the 9 importers need no edits.
 *
 * ## The restyle is structural, not cosmetic
 *
 * Stock shadcn laid items out as `border-b last:border-b-0` stacked dividers with
 * `hover:underline` on the trigger. Figma shows **discrete cards** with vertical gaps between
 * them and no hover underline. So the divider layout is gone, not tweaked.
 *
 * ## Measured treatment
 *
 * | Part | Value |
 * |---|---|
 * | grey item (Figma `Variant=Default`) | `--muted` #f5f5f5 fill, **no border** |
 * | white item (`Variant=White`) | `--background` white + 1px `--border` #d6d6d6 |
 * | item padding | 24px (`--spacing-6`), all sides |
 * | header row | 16px gap, title 18px bold, description 16px `--muted-foreground` |
 * | content offset | 24px above the content |
 * | indicator | 48px control, 24px glyph |
 *
 * **Closed height depends on whether a description is present**, which the Figma symbol's 99px
 * quietly assumes. Measured against a real browser (`storybook:measure-styles`):
 *
 * - **with** a description: 24 + 51 + 24 = **99px**, matching Figma. The 51px is the text
 *   column — a 27px title over a 24px description — which exceeds the 48px indicator.
 * - **without** one: 24 + 48 + 24 = **96px**. The 48px indicator now sets the row height,
 *   because a lone 18px title's line box is only ~23px.
 *
 * So 99px is not a property of a closed item; it is a property of a closed item *with a
 * description*. Stated precisely because an earlier draft of this comment claimed a flat 99px
 * and the browser measurement disproved it.
 *
 * **The 24px content offset is `pt-6` on the content, not a flex `gap` on the item.** With a
 * gap, a *closed* item would still reserve 24px below its header, because Radix keeps the
 * content element mounted at height 0. Padding inside the collapsing element is clipped
 * instead, which is why stock shadcn does it this way too.
 *
 * **Grey is the default** because Figma names that variant `Default`. Consumers wanting the
 * bordered treatment pass `variant="white"`.
 *
 * ## Variants belong to the item, not to Card
 *
 * The white variant's border is the item's own `border` (Tailwind's built-in 1px), NOT
 * `border-ui` — that token applies to Card only, and composing Card here purely to get a
 * border would drag in its shape tokens and its slot semantics for nothing.
 *
 * ## Not built, and worth knowing about
 *
 * - **`Size=Small`** is a third Figma axis (69px closed against 99px). Out of scope for
 *   CRCB2B-631, which specifies only the two background variants. Flagged, not built.
 *
 * ## The `flush` treatment (CRCB2B-348)
 *
 * The SRAM **Filters** pattern (`6952:46600`) composes this accordion but draws its groups
 * contiguously — no card, no fill, one hairline between them — which is exactly the layout the
 * CRCB2B-631 restyle above replaced. Rather than fork the primitive, `treatment="flush"` restores
 * that layout as an opt-in, and `variant="transparent"` drops the fill. `card` and `grey` remain
 * the defaults, so all 9 existing importers are untouched.
 *
 * **`treatment` is layout only** — inter-item gap and the divider. It deliberately does NOT carry
 * the Filters pattern's own header values (16px regular label against this component's 18px bold,
 * and a 32px indicator against 48px). Those are one consumer's type ramp, not a property of being
 * flush, so the filter group passes them as `className`. Folding them in here would make `flush`
 * mean "styled like the filter rail", which the next consumer would have to fight.
 *
 * Because the gap lives on the root and the divider is drawn between siblings, both are set by the
 * root and reach the items through `[data-slot=accordion-item]` — so a consumer opts in once
 * instead of passing `treatment` to every item and risking a mismatched pair.
 * - **`Decoration` (40px) and `Slot` (48px)** are optional header slots in Figma, both hidden
 *   in every example. Not built; a consumer can pass them as trigger children.
 * - **Figma swaps `keyboard_arrow_down` for `keyboard_arrow_up`**; this rotates the one glyph
 *   180°, which renders identically and keeps a single glyph in the icon subset.
 * - **Figma draws the toggle as an icon button**; the whole header row is the trigger here,
 *   which is a strictly larger target and the standard accordion pattern.
 */

const accordionItemVariants = cva('overflow-hidden', {
    variants: {
        variant: {
            /** Figma `Variant=Default`. Fill, no border. */
            grey: 'bg-muted',
            /** Figma `Variant=White`. White surface with a visible 1px rule. */
            white: 'bg-background border border-border',
            /**
             * Neither fill nor border, so the surface behind shows through. Pairs with
             * `treatment="flush"` on the root, whose divider is then the item's only boundary.
             * Figma `Filters` (`6952:46600`) for CRCB2B-348.
             */
            transparent: 'bg-transparent',
        },
    },
    defaultVariants: {
        variant: 'grey',
    },
});

const accordionVariants = cva('flex flex-col', {
    variants: {
        treatment: {
            /**
             * Discrete cards with separation. The 16px gap is DERIVED — Figma ships the item
             * symbols but defines no inter-item spacing.
             */
            card: 'gap-4',
            /**
             * Contiguous rows separated by one hairline, per Figma `Filters` (`6952:46600`).
             * Set on the root because both the gap and a between-siblings divider are properties
             * of the stack, not of an item in isolation — `last-child` cannot be resolved by an
             * item that does not know its position.
             */
            flush: [
                'gap-0',
                '[&>[data-slot=accordion-item]]:border-b [&>[data-slot=accordion-item]]:border-border',
                '[&>[data-slot=accordion-item]:last-child]:border-b-0',
            ],
        },
    },
    defaultVariants: {
        treatment: 'card',
    },
});

function Accordion({
    className,
    treatment,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root> & VariantProps<typeof accordionVariants>) {
    return (
        <AccordionPrimitive.Root
            data-slot="accordion"
            className={cn(accordionVariants({ treatment }), className)}
            {...props}
        />
    );
}

function AccordionItem({
    className,
    variant,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & VariantProps<typeof accordionItemVariants>) {
    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn(accordionItemVariants({ variant }), className)}
            {...props}
        />
    );
}

function AccordionTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn(
                    // `leading-normal` is required, not cosmetic: Figma's title is `body/lg-bold`
                    // at line-height 1.5, but `--text-lg` is tokenised as `heading/2xs` at 1.3.
                    // Without it the title's line box is 23.4px instead of 27px, which measured
                    // as a 3px-short item — the same reason `tabs.tsx` and `progress.tsx` pair
                    // `text-lg` with `leading-normal`.
                    'flex flex-1 items-center gap-4 p-6 text-left text-lg leading-normal font-bold tracking-[0.03em] text-foreground transition-colors',
                    'outline-none focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2',
                    'disabled:pointer-events-none disabled:text-disabled-foreground',
                    '[&[data-state=open]>[data-slot=accordion-indicator]]:rotate-180',
                    className
                )}
                {...props}>
                <span className="flex min-w-0 flex-1 flex-col">{children}</span>
                <span
                    data-slot="accordion-indicator"
                    // 48px control around a 24px glyph, per Figma's icon button.
                    className="flex size-12 shrink-0 items-center justify-center transition-transform duration-200">
                    <Icon name="keyboard_arrow_down" size={24} />
                </span>
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    );
}

/**
 * Optional secondary line beneath the title. Renders nothing when omitted, so no space is
 * reserved for it.
 */
function AccordionDescription({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="accordion-description"
            className={cn('block text-base font-book tracking-[0.03em] text-muted-foreground', className)}
            {...props}
        />
    );
}

function AccordionContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-base tracking-[0.03em]"
            {...props}>
            {/*
             * No top padding: the trigger's own `pb-6` is the 24px gap Figma draws between the
             * header and the content. Adding `pt-6` here would make it 48px. The bottom 24px is
             * the item's lower padding, and it lives inside the collapsing element so a closed
             * item clips it rather than reserving it.
             *
             * With a description the open height therefore lands at 24 + 51 + 24 + content + 24,
             * which is Figma's 263px for a 140px content region. See the note on the header row
             * above for why a description-less item is 3px shorter.
             */}
            <div className={cn('px-6 pb-6', className)}>{children}</div>
        </AccordionPrimitive.Content>
    );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent, AccordionDescription };
